import { useEffect, useMemo, useState } from 'react';
import QRCode from 'qrcode';
import { Check, Copy, Eye, QrCode, RefreshCw, ShieldAlert, Trash2 } from 'lucide-react';
import { DataTable, type DataTableColumn } from '../components/DataTable';
import { ProvisioningTabs } from '../components/ProvisioningTabs';
import { useDashboardData } from '../hooks/useDashboardData';
import { api } from '../utils/api';
import type { Device } from '../types';
import './Devices.css';

type DeviceAction = 'view' | 'move' | 'force-release' | 'retire' | 'qr';

export function Devices() {
  const { isSuperAdmin, sites, units, refetch: refetchDashboard } = useDashboardData();
  const [devices, setDevices] = useState<Device[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [selected, setSelected] = useState<Device | null>(null);
  const [action, setAction] = useState<DeviceAction | null>(null);
  const [toSiteId, setToSiteId] = useState('');
  const [gatewayId, setGatewayId] = useState('');
  const [qrUrl, setQrUrl] = useState('');
  const [qrDataUrl, setQrDataUrl] = useState('');
  const [actionError, setActionError] = useState('');
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);

  const loadDevices = async () => {
    setLoading(true);
    setLoadError('');
    try { setDevices(await api.get('/api/v1/devices')); }
    catch (error) { setLoadError(error instanceof Error ? error.message : 'Could not load devices'); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    if (!isSuperAdmin) return;
    let cancelled = false;
    api.get('/api/v1/devices')
      .then((rows) => { if (!cancelled) setDevices(rows); })
      .catch((error) => { if (!cancelled) setLoadError(error instanceof Error ? error.message : 'Could not load devices'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [isSuperAdmin]);

  const openAction = async (device: Device, nextAction: DeviceAction) => {
    setSelected(device);
    setAction(nextAction);
    setActionError('');
    setToSiteId('');
    setGatewayId('');
    setQrUrl('');
    setQrDataUrl('');
    setCopied(false);
    if (nextAction === 'qr') {
      setBusy(true);
      try {
        const result = await api.post(`/api/v1/devices/${device.deviceUuid}/regenerate-qr`);
        setQrUrl(result.qrUrl);
        setQrDataUrl(await QRCode.toDataURL(result.qrUrl, { width: 320, margin: 2 }));
      } catch (error) {
        setActionError(error instanceof Error ? error.message : 'Could not regenerate QR');
      } finally { setBusy(false); }
    }
  };

  const closeAction = () => {
    if (busy) return;
    setSelected(null);
    setAction(null);
    setActionError('');
  };

  const runAction = async () => {
    if (!selected || !action) return;
    setBusy(true);
    setActionError('');
    try {
      if (action === 'move') {
        await api.post(`/api/v1/devices/${selected.deviceUuid}/move`, {
          toSiteId,
          ...(selected.role === 'leaf' ? { gatewayId } : {}),
        });
      } else if (action === 'force-release') {
        await api.post(`/api/v1/devices/${selected.deviceUuid}/force-release`);
      } else if (action === 'retire') {
        await api.post(`/api/v1/devices/${selected.deviceUuid}/retire`);
      }
      setSelected(null);
      setAction(null);
      await loadDevices();
      refetchDashboard();
    } catch (error) {
      setActionError(error instanceof Error ? error.message : 'Device action failed');
    } finally { setBusy(false); }
  };

  const columns = useMemo<DataTableColumn<Device>[]>(() => [
    { id: 'serial', header: 'Serial', value: (device) => device.serialNumber, sortable: true, filter: { placeholder: 'Filter serial…' }, render: (device) => <strong>{device.serialNumber}</strong> },
    { id: 'node', header: 'Node', value: (device) => device.nodeId, sortable: true, filter: { placeholder: 'Filter node…' }, render: (device) => device.nodeId },
    { id: 'role', header: 'Role', value: (device) => device.role, sortable: true, filter: { type: 'select', placeholder: 'All roles', options: [{ value: 'leaf', label: 'Leaf' }, { value: 'cellular', label: 'Cellular' }, { value: 'gateway', label: 'Gateway' }] }, render: (device) => device.role },
    { id: 'status', header: 'Status', value: (device) => device.lifecycleStatus, sortable: true, filter: { type: 'select', placeholder: 'All states', options: [{ value: 'manufactured', label: 'Manufactured' }, { value: 'adopted', label: 'Adopted' }, { value: 'released', label: 'Released' }, { value: 'retired', label: 'Retired' }, { value: 'physical_reprovision_required', label: 'Reprovision required' }] }, render: (device) => <span className={`device-status device-status--${device.lifecycleStatus}`}>{device.lifecycleStatus.replaceAll('_', ' ')}</span> },
    { id: 'rotation', header: 'Rotation', value: (device) => device.pendingRotation?.state ?? '', sortable: true, filter: { type: 'select', placeholder: 'Any rotation', options: [{ value: 'issued', label: 'Pending' }, { value: 'confirmed', label: 'Confirmed' }] }, render: (device) => device.pendingRotation ? `${device.pendingRotation.state} (gen ${device.pendingRotation.generation})` : '—' },
    { id: 'created', header: 'Created', value: (device) => new Date(device.createdAt), sortable: true, render: (device) => new Date(device.createdAt).toLocaleDateString() },
    { id: 'actions', header: 'Actions', render: (device) => <div className="device-actions">
      <button type="button" title="View device" onClick={() => openAction(device, 'view')}><Eye size={15} />View</button>
      <button type="button" title="Move device" onClick={() => openAction(device, 'move')} disabled={device.lifecycleStatus !== 'adopted' || Boolean(device.pendingRotation)}><RefreshCw size={15} />Move</button>
      <button type="button" title="Regenerate permanent QR" onClick={() => openAction(device, 'qr')}><QrCode size={15} />QR</button>
      <button type="button" title="Force release and invalidate secrets" onClick={() => openAction(device, 'force-release')} disabled={device.lifecycleStatus === 'retired'}><ShieldAlert size={15} />Force release</button>
      <button type="button" title="Retire device" onClick={() => openAction(device, 'retire')} disabled={device.lifecycleStatus === 'retired'}><Trash2 size={15} />Retire</button>
    </div> },
  ], []);

  const destinationGateways = units.filter((unit) => unit.siteId === toSiteId && unit.role === 'gateway');

  if (!isSuperAdmin) return <div className="devices-page"><h1>Device lifecycle</h1><p>This page is restricted to super administrators.</p></div>;

  return (
    <div className="devices-page">
      <ProvisioningTabs />
      <div className="devices-header"><div><h1>Device lifecycle</h1><p>View and operate every permanent device identity.</p></div><button type="button" onClick={loadDevices} disabled={loading}><RefreshCw size={16} />Refresh</button></div>
      {loadError && <p className="devices-error">{loadError}</p>}
      {loading ? <p>Loading devices…</p> : <DataTable rows={devices} columns={columns} getRowKey={(device) => device.deviceUuid} emptyMessage="No devices have been manufactured." initialSort={{ columnId: 'serial', direction: 'asc' }} />}

      {selected && action && (
        <div className="devices-modal" role="dialog" aria-modal="true" onClick={closeAction}><div className="devices-modal__card" onClick={(event) => event.stopPropagation()}>
          {action === 'view' && <><h2>{selected.serialNumber}</h2><dl className="device-detail"><div><dt>Device UUID</dt><dd>{selected.deviceUuid}</dd></div><div><dt>Node / role</dt><dd>{selected.nodeId} / {selected.role}</dd></div><div><dt>Lifecycle</dt><dd>{selected.lifecycleStatus}</dd></div><div><dt>Current binding</dt><dd>{selected.currentBindingId ?? 'None'}</dd></div><div><dt>Config version</dt><dd>{selected.configVersion}</dd></div><div><dt>Last config pull</dt><dd>{selected.lastConfigPullAt ? new Date(selected.lastConfigPullAt).toLocaleString() : 'Never'}</dd></div></dl></>}
          {action === 'move' && <><h2>Move {selected.serialNumber}</h2><p>Select the new site. A leaf must use a gateway in that destination site.</p><label>Destination site<select value={toSiteId} onChange={(event) => { setToSiteId(event.target.value); setGatewayId(''); }}><option value="">Choose a site…</option>{sites.map((site) => <option key={site.id} value={site.id}>{site.name}</option>)}</select></label>{selected.role === 'leaf' && <label>Destination gateway<select value={gatewayId} onChange={(event) => setGatewayId(event.target.value)} disabled={!toSiteId}><option value="">Choose a gateway…</option>{destinationGateways.map((unit) => <option key={unit.unitId} value={unit.unitId}>{unit.nodeName}</option>)}</select></label>}</>}
          {action === 'force-release' && <><h2>Force release {selected.serialNumber}?</h2><p>This invalidates all device secrets and marks the hardware as requiring physical re-provisioning. Use it only when normal release cannot complete.</p></>}
          {action === 'retire' && <><h2>Retire {selected.serialNumber}?</h2><p>Retirement is terminal. This permanent identity cannot be adopted again.</p></>}
          {action === 'qr' && <><h2>Permanent QR</h2>{busy && <p>Generating…</p>}{qrDataUrl && <div className="devices-qr"><img src={qrDataUrl} alt={`QR code for ${selected.serialNumber}`} /><p>{qrUrl}</p><button type="button" onClick={async () => { await navigator.clipboard.writeText(qrUrl); setCopied(true); }}>{copied ? <Check size={16} /> : <Copy size={16} />}{copied ? 'Copied' : 'Copy URL'}</button></div>}</>}
          {actionError && <p className="devices-error">{actionError}</p>}
          <div className="devices-modal__buttons"><button type="button" onClick={closeAction} disabled={busy}>{action === 'view' || action === 'qr' ? 'Close' : 'Cancel'}</button>{!['view', 'qr'].includes(action) && <button type="button" className={action === 'move' ? '' : 'danger'} onClick={runAction} disabled={busy || (action === 'move' && (!toSiteId || (selected.role === 'leaf' && !gatewayId)))}>{busy ? 'Working…' : action === 'move' ? 'Move device' : action === 'retire' ? 'Retire device' : 'Force release'}</button>}</div>
        </div></div>
      )}
    </div>
  );
}

export default Devices;
